import _ from 'underscore';
import Promise from 'bluebird';
import { odata } from '@bingads-webui/http-util';
import { serializeCSV, extractIds } from './bulkify';

const MAX_RECOMEND_TIMEOUT = 60 * 1000; // set recommend timeout to 60 seconds

export const parseImageFitResponse = (res) => {
  const ret = {
    expandedImageUrl: res.ImageUrl,
    imageExpansionType: res.ImageExpansionType, // None/WhitePadding/SingleColorPadding/Outpainting

    // [{aspectRatio:double, cropSetting: {sourceHeight, sourceWidth, sourceX, sourceY}}]
    originalImageCropSettings: _.map(res.OriginalImageCropSetting, crop => ({
      AspectRatio: crop.aspectRatio,
      CropSetting: {
        SourceHeight: crop.cropSetting && crop.cropSetting.sourceHeight,
        SourceWidth: crop.cropSetting && crop.cropSetting.sourceWidth,
        SourceX: crop.cropSetting && crop.cropSetting.sourceX,
        SourceY: crop.cropSetting && crop.cropSetting.sourceY,
      },
    })),
    // [{aspectRatio:double, cropSetting: {sourceHeight, sourceWidth, sourceX, sourceY}}]
    expandedImageCropSettings: _.map(res.ImageCropSetting, crop => ({
      AspectRatio: crop.aspectRatio,
      CropSetting: {
        SourceHeight: crop.cropSetting && crop.cropSetting.sourceHeight,
        SourceWidth: crop.cropSetting && crop.cropSetting.sourceWidth,
        SourceX: crop.cropSetting && crop.cropSetting.sourceX,
        SourceY: crop.cropSetting && crop.cropSetting.sourceY,
      },
    })),
    expandedImageWidth: res.ImageDimension && res.ImageDimension.w,
    expandedImageHeight: res.ImageDimension && res.ImageDimension.h,
    paddingColor: res.PaddingColor,
  };
  return ret;
};

export class ImageOData {
  constructor(cid, aid, oDataConfig, timeout) {
    this.cid = cid;
    this.aid = aid;
    this.baseURL = `${oDataConfig.odataPath}/Customers(${this.cid})/Accounts(${this.aid})/`;
    this.orderByDefault = 'UploadedTime';
    this.orderDefault = 'desc';
    this.oDataConfig = {
      oDataToken: oDataConfig.odataToken,
      oDataTokenType: oDataConfig.oDataTokenType,
      applicationName: oDataConfig.applicationName,
      beforeSend: oDataConfig.beforeSend,
    };
    this.maxTimeout = timeout || MAX_RECOMEND_TIMEOUT;
  }

  /**
   * Gets previously uploaded images (20).
   * @param {number} sortById - id for the sort criteria
   * @param {string} searchTerm - search term for query
   * @return {Promise} Response from the oData call.
   */
  getExistingImages({
    sortQuery,
    searchTerm,
    pageNumber,
    pageSize,
    filterTerm = '',
  }) {
    const top = pageSize;
    const skip = pageNumber * pageSize;

    const filterArray = [];

    if (searchTerm && searchTerm.length > 0) {
      filterArray.push(`contains(Text, '${searchTerm}')`);
    }

    if (filterTerm && filterTerm.length > 0) {
      filterArray.push(filterTerm);
    }

    const filterQuery = filterArray.length > 0 ? `&$filter=${filterArray.join(' and ')}` : '';

    const url = `${this.baseURL}/Images?mediaenabledentityfilter=ResponsiveAd&${sortQuery}${filterQuery}`;

    const query = {
      $top: top,
      $skip: skip,
      count: true,
    };

    return odata.get(url, _.extend({ data: query }, this.oDataConfig));
  }

  /**
   * Gets previously uploaded images
   * @param {string} sortBy - sort by
   * @param {string} sortDirection - 'asc'/'desc',
   * @param {string} searchTerm - search term for query
   * @param {number} minWidth - filter by min-width
   * @param {number} minHeight - filter by min-height
   * @param {string} dateAddedStart - filter by UploadedTime
   * @param {string} dateAddedEnd - filter by UploadedTime
   * @param {string} sourceType - 'Unknown', 'UserUploaded', 'GoogleImported',
   * @param {number} skip,
   * @param {number} count,
   * @return {Promise} Response from the oData call.
   */
  getImages({
    sortBy,
    sortDirection,
    searchTerm,
    minWidth,
    minHeight,
    dateAddedStart,
    dateAddedEnd,
    sourceType,
    skip,
    count,
  }) {
    // compose sort query
    let sortQuery = sortBy ? `$orderby=${sortBy}` : '';
    if (sortQuery && sortDirection) {
      sortQuery += ` ${sortDirection}`;
    }

    // $filter
    const filters = [];
    if (searchTerm) {
      // use BroadSearchFields to search both image name and image id
      filters.push(`contains(BroadSearchFields, '${searchTerm}')`);
    }
    if (minWidth) {
      filters.push(`Width gt ${minWidth}`);
    }
    if (minHeight) {
      filters.push(`Height gt ${minHeight}`);
    }
    if (dateAddedStart && dateAddedEnd && dateAddedStart === dateAddedEnd) {
      filters.push(`UploadedTime eq ${dateAddedStart}`);
    } else {
      if (dateAddedStart) {
        filters.push(`UploadedTime ge ${dateAddedStart}`);
      }
      if (dateAddedEnd) {
        filters.push(`UploadedTime le ${dateAddedEnd}`);
      }
    }
    if (sourceType) {
      filters.push(`SourceType eq '${sourceType}'`);
    }
    let searchQuery = filters.join(' and ');
    if (searchQuery) {
      searchQuery = `$filter=${searchQuery}`;
    }

    const params = sortQuery && searchQuery ? `${sortQuery}&${searchQuery}` : (sortQuery || searchQuery);
    const url = `${this.baseURL}Images?mediaenabledentityfilter=ResponsiveAd&${params}`;

    const query = {
      $top: count,
      $skip: skip,
      count: true,
    };

    return odata.get(url, _.extend({ data: query }, this.oDataConfig));
  }

  /**
   * Gets image specified by the id.
   * @param {number} imageId Id of the image to be requested.
   * @param {boolean} isImageFittingEnabled - if the image fitting is enabled or not
   * @param {string} adType - for different adType, MT returns image fittings with different assets ratios
   * @return {Promise} Response from the oData call.
   */
  getImageByIdAsync(imageId, isImageFittingEnabled, adType = 'Audience') {
    let url = `${this.baseURL}Images(${imageId})`;
    if (isImageFittingEnabled) {
      url = `${url}?AdType=${adType}`;
    }

    return odata.get(url, this.oDataConfig);
  }

  uploadImageAsync(imageData, activityLogger = null, inProgress = null, isLogo = false, name = '') { // eslint-disable-line max-len
    if (activityLogger) {
      // Log upload image data
      const dataToLog = JSON.stringify(imageData).substr(0, 100);

      activityLogger.trace(JSON.stringify(dataToLog));
    }

    let processedImageData = imageData;

    const base64 = 'base64,';
    const base64Index = imageData.indexOf(base64);

    processedImageData = base64Index > -1
      ? imageData.substr(base64Index + base64.length)
      : imageData;

    const url = `${this.baseURL}Images`;

    const requestBody = {
      Data: processedImageData,
      Type: isLogo ? 'Logo' : 'GenericImage',
    };

    if (name) {
      requestBody.Text = name;
    }

    return odata.post(url, _.extend({ data: requestBody, progress: inProgress }, this.oDataConfig))
      .then(
        image => ({ imageId: image.value, errors: [] }),
        error => this.processUploadSingleImageRejection(error, activityLogger)
      );
  }

  updateImageAsync(imageData) {
    const url = `${this.baseURL}Images(${imageData.Id})`;

    const requestBody = {
      Text: imageData.Text,
      Type: imageData.Type,
      Url: imageData.Url,
      Data: imageData.Data,
    };

    return odata.patch(url, _.extend({ data: requestBody }, this.oDataConfig));
  }

  deleteImageAsync(imageData) {
    const url = `${this.baseURL}Images(${imageData.IdString})`;

    return odata.delete(url, this.oDataConfig);
  }

  /**
   * Cleans the error response object and sets error messages into an array.
   * @param {object} error Object with errors returned by the oData call.
   * @param {Object} activityLogger - logger object for activity
   *
   * @return {array} Array of error messages.
   */
  processUploadSingleImageRejection(error, activityLogger) {
    // const errMsg = this.i18nErrors.getODataErrors(error.body.value);
    // _.each(errMsg, (err) => {
    //   errors.push(err.message);
    // });

    if (activityLogger) {
      activityLogger.error(JSON.stringify(error));
    }

    return error && error.body && error.body.value;
  }

  /**
   * Save image asset by url and meta data,
   * @param {array} imageInfos Array of image info including url and metaData
   *
   * @return {Promise} Array of image ids. Different metaData will generate different image id.
   */
  bulkSaveImagesByUrl = (imageInfos) => {
    const url = `${this.baseURL}/Default.BulkUpsert`;
    const csvRows = serializeCSV(imageInfos, this.aid);

    if (_.isEmpty(csvRows)) {
      return Promise.resolve([]);
    }

    return odata.post(url, _.defaults({
      data: { CsvRows: csvRows },
    }, this.oDataConfig)).timeout(this.maxTimeout)
      .then((res) => {
        if (_.isEmpty(res)) {
          return [];
        }

        return extractIds(res);
      });
  };

  addAsset = ({
    url,
    sourceType,
  }) => {
    const requestUrl = `${this.baseURL}Folders/Default.AddAsset`;
    const requestBody = {
      FolderId: null,
      Asset: {
        '@odata.type': '#Model.Image',
        Url: url,
        Type: 'GenericImage',
        SourceType: sourceType,
      },
    };

    return odata.post(requestUrl, _.extend({ data: requestBody }, this.oDataConfig))
      .then(res => res.value);
  };

  imageFit = ({
    imageUrl,
    placements, // [{ aspectRatio: doulbe, minHeight: int }]
    skipOutpainting = false,
  }) => {
    const url = `${this.baseURL}Images/Default.FitImageWithCustomCrop?enableOutpainting=${!skipOutpainting}`;
    const requestBody = {
      ImageUrl: imageUrl,
      Placements: placements,
    };

    const ret = odata.post(url, _.extend({ data: requestBody }, this.oDataConfig)).then((res) => {
      // map the property names of response data
      const value = parseImageFitResponse(res);
      return value;
    });
    return ret;
  };
}
